import React, { useState, useMemo } from "react";
import "./PricingCalcPage.scss";
import { projectPricing } from "../../Utility/ProjectPricing";
import { USD_TO_AUD_RATE } from "../../Utility/Constants";
import { Fade } from "react-awesome-reveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faDollarSign,
  faCalculator,
  faChevronDown,
  faChevronUp,
  faTimes,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

type Currency = "USD" | "AUD";

const PricingCalcPage = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [currency, setCurrency] = useState<Currency>("USD");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [showContactForm, setShowContactForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleItemToggle = (itemId: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const calculateTotal = useMemo(() => {
    const baseFee = currency === "USD" ? 0 : 0 * USD_TO_AUD_RATE;
    let itemsTotal = 0;

    projectPricing.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (selectedItems.has(item.id)) {
          itemsTotal +=
            currency === "USD"
              ? item.priceUsd
              : item.priceUsd * USD_TO_AUD_RATE;
        }
      });
    });
    if (itemsTotal > 0) {
      return baseFee + itemsTotal;
    } else {
      return 0;
    }
  }, [selectedItems, currency]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleGetStarted = () => {
    setShowContactForm(true);
  };

  const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Collect selected items details
      const selectedItemsDetails: Array<{
        id: string;
        name: string;
        priceUsd: number;
      }> = [];

      projectPricing.categories.forEach((category) => {
        category.items.forEach((item) => {
          if (selectedItems.has(item.id)) {
            selectedItemsDetails.push({
              id: item.id,
              name: item.name,
              priceUsd: item.priceUsd,
            });
          }
        });
      });

      // Build table message
      let messageTable = "Selected Items:<br/>";
      messageTable +=
        '<table style="border-collapse: collapse; width: 100%;"><thead><tr><td style="border: 1px solid #ccc; padding: 8px;">ID</td><td style="border: 1px solid #ccc; padding: 8px;">Name</td><td style="border: 1px solid #ccc; padding: 8px;">Price (USD)</td></tr></thead><tbody>';
      selectedItemsDetails.forEach((item) => {
        messageTable += `<tr><td style="border: 1px solid #ccc; padding: 8px;">${item.id}</td><td style="border: 1px solid #ccc; padding: 8px;">${item.name}</td><td style="border: 1px solid #ccc; padding: 8px;">$${item.priceUsd}</td></tr>`;
      });
      messageTable += "</tbody></table>";
      messageTable += `<br/>Total: ${formatPrice(calculateTotal)} (${currency})`;

      // Add user message if provided
      if (message) {
        messageTable += `<br/><strong>Additional Information:</strong><br/>${message}`;
      }

      // Prepare the request body
      const requestBody = {
        type: "silveridea-calc",
        name: name,
        email: email,
        phone: phone,
        message: messageTable,
      };

      // Make POST request
      const response = await fetch(
        "https://pocketbase.rtcloud.com.au/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (response.ok) {
        // Show success modal
        setShowSuccessModal(true);
        // Reset form
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setShowContactForm(false);
      } else {
        alert("Failed to submit request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting contact:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pricing-calc-page">
      {/* Back Button */}
      <div className="back-button-container">
        <button onClick={() => navigate(-1)} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <Fade triggerOnce duration={1000} direction="up">
            <div className="hero-content">
              <div className="hero-header">
                <FontAwesomeIcon icon={faCalculator} className="hero-icon" />
                <h1 className="hero-title">Price Estimate Calculator</h1>
              </div>
              <div>
                <p className="hero-subtitle">
                  Select the features you need and get an instant estimate for
                  your project
                </p>
              </div>
            </div>
          </Fade>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="calculator-section">
        <div className="container">
          <div className="calculator-layout">
            {/* Left Side - Categories */}
            <div className="categories-panel">
              {projectPricing.categories.map((category, catIndex) => {
                if (category.items.length === 0) return null;
                const isExpanded = expandedCategories.has(category.id);

                return (
                  <Fade
                    key={category.id}
                    triggerOnce
                    duration={1000}
                    delay={catIndex * 100}
                    direction="up"
                  >
                    <div className="category-card">
                      <div
                        className="category-header"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className="category-info">
                          <h3 className="category-title">{category.name}</h3>
                          <p className="category-description">
                            {category.description}
                          </p>
                        </div>
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="collapse-icon"
                        />
                      </div>

                      {isExpanded && (
                        <div className="items-list">
                          {category.items.map((item) => {
                            const isSelected = selectedItems.has(item.id);

                            return (
                              <div
                                key={item.id}
                                className={`item-card ${isSelected ? "selected" : ""}`}
                                onClick={() => handleItemToggle(item.id)}
                              >
                                <div className="item-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleItemToggle(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="checkmark">
                                    {isSelected && (
                                      <FontAwesomeIcon icon={faCheck} />
                                    )}
                                  </span>
                                </div>

                                <div className="item-info">
                                  <h4 className="item-name">{item.name}</h4>
                                  <p className="item-description">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Fade>
                );
              })}
            </div>

            {/* Right Side - Summary */}
            <div className="summary-panel">
              <Fade triggerOnce duration={1000} delay={200} direction="left">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FontAwesomeIcon icon={faDollarSign} />
                    Project Estimate
                  </h3>

                  {/* Currency Switcher */}
                  <div className="currency-switcher">
                    <button
                      className={`currency-btn ${currency === "USD" ? "active" : ""}`}
                      onClick={() => setCurrency("USD")}
                    >
                      USD
                    </button>
                    <button
                      className={`currency-btn ${currency === "AUD" ? "active" : ""}`}
                      onClick={() => setCurrency("AUD")}
                    >
                      AUD
                    </button>
                  </div>

                  {/* Selected Items Count */}
                  <div className="summary-info">
                    <div className="info-row">
                      <span>Selected Features:</span>
                      <span className="info-value">{selectedItems.size}</span>
                    </div>
                  </div>

                  <div className="summary-divider"></div>

                  {/* Total */}
                  <div className="summary-total">
                    <span className="total-label">Total Estimate:</span>
                    <span className="total-amount">
                      {formatPrice(calculateTotal)}
                    </span>
                  </div>

                  <p className="summary-note">
                    * This is an estimated price. Final pricing may vary based
                    on project complexity and specific requirements.
                  </p>

                  {!showContactForm ? (
                    <button className="contact-btn" onClick={handleGetStarted}>
                      Get Started
                      <span className="btn-arrow">→</span>
                    </button>
                  ) : (
                    <form className="contact-form" onSubmit={handleSend}>
                      <p className="contact-form-text">
                        Add your email and we will contact you shortly
                      </p>
                      <div className="form-group">
                        <input
                          name="name"
                          required
                          type="text"
                          className="form-input"
                          placeholder="Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <input
                          name="email"
                          required
                          type="email"
                          className="form-input"
                          placeholder="Email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <input
                          name="tel"
                          type="tel"
                          className="form-input"
                          placeholder="Phone (optional)"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <textarea
                          name="message"
                          className="form-input form-textarea"
                          placeholder="Tell us more about your app"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={4}
                        />
                      </div>
                      <button
                        type="submit"
                        className="contact-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Send"}
                        {!isSubmitting && <span className="btn-arrow">→</span>}
                      </button>
                    </form>
                  )}
                </div>
              </Fade>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSuccessModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => {
                // Event snippet for Page view conversion page
                (window as any).gtag("event", "conversion", {
                  send_to: "AW-18010766468/Yk6pCO2SyoccEIT5mYxD",
                });
                setShowSuccessModal(false);
                navigate("/calculator?submitted=true");
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="modal-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <h3 className="modal-title">Request Submitted Successfully!</h3>
            <p className="modal-message">
              Your request was submitted successfully. We will be in contact
              shortly.
            </p>
            <button
              className="modal-btn"
              onClick={() => setShowSuccessModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingCalcPage;
